import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { Music2, Zap, Play, Pause, ArrowLeft, Sparkles, Edit, SkipBack, SkipForward, ListPlus, ListCheck, Loader2, Heart, ChevronDown, ChevronUp, Bug } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Slider } from '@/components/ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ZapButton } from '@/components/ZapButton';
import { LikeButton } from '@/components/LikeButton';
import { ShareButton } from '@/components/ShareButton';
import { CommentsSection } from '@/components/comments/CommentsSection';
import { PlayerBar } from '@/components/PlayerBar';
import { Navigation } from '@/components/Navigation';
import { UploadTrackDialog } from '@/components/UploadTrackDialog';
import { AddToPlaylistDialog } from '@/components/AddToPlaylistDialog';
import { useMusicTrack } from '@/hooks/useMusicTrack';
import { useAuthor } from '@/hooks/useAuthor';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useComments } from '@/hooks/useComments';
import { useTrackZaps, formatSats } from '@/hooks/useTrackZaps';
import { useLikes } from '@/hooks/useLikes';
import { useMusicPlayer } from '@/contexts/MusicPlayerContext';
import { useIsMobile } from '@/hooks/useIsMobile';
import { usePlaylists } from '@/hooks/usePlaylists';
import { genUserName } from '@/lib/genUserName';
import { formatDuration, MUSIC_TRACK_KIND } from '@/lib/musicTypes';

export function TrackPage() {
  const { nip19: nip19Param } = useParams<{ nip19: string }>();
  const navigate = useNavigate();
  const [decodedAddr, setDecodedAddr] = useState<{
    pubkey: string;
    identifier: string;
    kind: number;
  } | null>(null);

  // Decode naddr
  useEffect(() => {
    if (!nip19Param) return;

    try {
      const decoded = nip19.decode(nip19Param);
      if (decoded.type === 'naddr') {
        setDecodedAddr({
          pubkey: decoded.data.pubkey,
          identifier: decoded.data.identifier,
          kind: decoded.data.kind,
        });
      }
    } catch {
      // Invalid naddr
      navigate('/404');
    }
  }, [nip19Param, navigate]);

  const { data: track, isLoading } = useMusicTrack(
    decodedAddr?.pubkey || '',
    decodedAddr?.identifier || ''
  );

  const uploader = useAuthor(track?.pubkey || '');

  // Use artist pubkey if available, otherwise use uploader pubkey
  const artistPubkey = track?.artistPubkey || track?.pubkey || '';
  const artist = useAuthor(artistPubkey);

  const { user } = useCurrentUser();
  const { data: playlists } = usePlaylists({ pubkey: user?.pubkey });
  const { data: zaps } = useTrackZaps(
    track?.event.kind || 0,
    track?.pubkey || '',
    track?.id || ''
  );
  const { data: commentsData } = useComments(track?.event || new URL('about:blank'));
  const { data: likes } = useLikes(track?.event.id || '');
  const {
    currentTrack,
    isPlaying,
    playTrack,
    togglePlay,
    currentTime,
    duration,
    seek,
    nextTrack,
    previousTrack,
    hasNext,
    hasPrevious,
  } = useMusicPlayer();
  const isMobile = useIsMobile();
  const [isDebugOpen, setIsDebugOpen] = useState(false);

  // Use artist name if available, otherwise use artist's profile name, or fallback to generated name
  const artistMetadata = artist.data?.metadata;
  const displayName = track?.artistName || artistMetadata?.name || genUserName(artistPubkey);
  const avatarUrl = artistMetadata?.picture;

  // Check if current user is the creator of this track
  const isCreator = user?.pubkey === track?.pubkey;

  // Check if track is in any playlist
  const trackRef = track ? `${MUSIC_TRACK_KIND}:${track.pubkey}:${track.id}` : '';
  const isInPlaylist = playlists?.some((p) => p.trackRefs.includes(trackRef)) ?? false;

  // Calculate zap stats
  const totalZaps = zaps?.reduce((sum, zap) => sum + zap.amount, 0) || 0;
  const zapCount = zaps?.length || 0;

  // Get top zappers
  const topZappers = zaps?.slice(0, 10) || [];

  // Get comment count
  const commentCount = commentsData?.allComments?.length || 0;

  // Get like count
  const likeCount = likes?.length || 0;

  // Check if track has lyrics (from lyrics tag)
  const lyricsTag = track?.event.tags.find(([name]) => name === 'lyrics')?.[1];
  const hasLyrics = lyricsTag && lyricsTag.trim().length > 0;

  if (!track) {
    return <TrackPageSkeleton />;
  }

  // Background style based on album art or gradient
  const backgroundStyle = track.imageUrl
    ? { backgroundImage: `url(${track.imageUrl})` }
    : track.gradient
    ? { background: `linear-gradient(135deg, ${track.gradient.join(', ')})` }
    : { background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' };

  const isCurrentTrack = currentTrack?.event.id === track.event.id;
  const isCurrentlyPlaying = isCurrentTrack && isPlaying;

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Global Navigation */}
      <Navigation />

      {/* Hero Section with blurred background */}
      <div className="relative">
        {/* Blurred background - fixed height */}
        <div
          className="absolute top-0 left-0 right-0 h-[50vh] bg-cover bg-center blur-3xl opacity-45"
          style={backgroundStyle}
        />

        {/* Content */}
        <div className="relative mx-auto px-4 py-8 max-w-4xl 2xl:max-w-5xl">

          <div className="grid md:grid-cols-[300px,1fr] gap-8 max-w-lg md:max-w-none mx-auto">
            {/* Album Art */}
            <div
              className="w-full aspect-square rounded-lg bg-cover bg-center shadow-2xl transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_25px_50px_-12px_rgba(0,0,0,0.5)]"
              style={backgroundStyle}
            >
              {!track.imageUrl && (
                <div className="h-full flex items-center justify-center bg-black/20 rounded-lg">
                  <Music2 className="h-16 w-16 md:h-24 md:w-24 text-white/80" />
                </div>
              )}
            </div>

            {/* Right Column: Track Info + Tabs */}
            <div className="space-y-8">
              {/* Track Info */}
              <div className="space-y-6">
                {/* Title and Artist */}
                <div>
                  <h1 className="text-2xl md:text-4xl font-bold mb-2">{track.title}</h1>
                  {track.artistPubkey ? (
                    <Link
                      to={`/${nip19.npubEncode(artistPubkey)}`}
                      className="text-muted-foreground hover:text-foreground transition-colors inline-block mb-3"
                    >
                      {/* Desktop: Show avatar + name */}
                      <div className="hidden md:flex items-center gap-2">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={avatarUrl} alt={displayName} />
                          <AvatarFallback>
                            {displayName.slice(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-lg">{displayName}</span>
                      </div>
                      {/* Mobile: Show name only */}
                      <span className="md:hidden text-base">{displayName}</span>
                    </Link>
                  ) : (
                    <div className="text-muted-foreground mb-3">
                      {/* Desktop: Show name only (no avatar) */}
                      <span className="text-lg">{displayName}</span>
                    </div>
                  )}
                </div>

                {/* Mobile Player Controls - Always show on mobile */}
                {isMobile && (
                  <div className="space-y-4">
                    {/* Controls */}
                    <div className="flex items-center justify-center gap-3">
                      {/* Previous Button */}
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={previousTrack}
                        disabled={!hasPrevious}
                        className="h-12 w-12 rounded-full border-0 bg-white/[0.06] hover:bg-white/[0.12] disabled:opacity-30"
                      >
                        <SkipBack className="h-5 w-5" />
                      </Button>

                      {/* Play/Pause */}
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={isCurrentTrack ? togglePlay : () => playTrack(track)}
                        className="h-16 w-16 rounded-full border-0 bg-white text-black hover:bg-white/90 [&_svg]:!text-black"
                      >
                        {isCurrentlyPlaying ? (
                          <Pause className="h-7 w-7" />
                        ) : (
                          <Play className="h-7 w-7 ml-0.5" />
                        )}
                      </Button>

                      {/* Next Button */}
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={nextTrack}
                        disabled={!hasNext}
                        className="h-12 w-12 rounded-full border-0 bg-white/[0.06] hover:bg-white/[0.12] disabled:opacity-30"
                      >
                        <SkipForward className="h-5 w-5" />
                      </Button>
                    </div>

                    {/* Progress Bar - Only show when track is playing */}
                    {isCurrentTrack && (
                      <div className="space-y-1">
                        <Slider
                          variant="progress"
                          value={[currentTime]}
                          max={duration || 100}
                          step={0.1}
                          onValueChange={([value]) => seek(value)}
                          className="cursor-pointer"
                        />
                        <div className="flex items-center justify-between text-xs text-muted-foreground px-1">
                          <span>{formatDuration(currentTime)}</span>
                          <span>{formatDuration(duration)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Genres and Duration */}
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    {track.isAiGenerated && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Badge variant="secondary" className="gap-1">
                              <Sparkles className="h-3 w-3" />
                              AI
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>This track was assisted in part or generated in full by AI</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                    {track.genres.map((genre) => (
                      <Badge key={genre} variant="secondary">
                        {genre}
                      </Badge>
                    ))}
                    {track.duration && (
                      <Badge variant="outline">{formatDuration(track.duration)}</Badge>
                    )}
                  </div>

                  {/* Action Buttons - Mobile only */}
                  {isMobile && (
                    <div className="flex items-center gap-2">
                      <LikeButton
                        event={track.event}
                        size="icon"
                        className="h-12 w-12 bg-white/[0.06] hover:bg-white/[0.12]"
                      />
                      <ShareButton
                        track={track}
                        size="icon"
                        variant="ghost"
                        className="h-12 w-12 rounded-full bg-white/[0.06] hover:bg-white/[0.12]"
                      />
                      {user && (
                        <AddToPlaylistDialog
                          track={track}
                          trigger={
                            <Button
                              size="icon"
                              variant="ghost"
                              className={`h-12 w-12 rounded-full ${
                                isInPlaylist
                                  ? 'bg-amber-500/20 hover:bg-amber-500/30 text-amber-500'
                                  : 'bg-white/[0.06] hover:bg-white/[0.12]'
                              }`}
                            >
                              {isInPlaylist ? (
                                <ListCheck className="h-5 w-5" />
                              ) : (
                                <ListPlus className="h-5 w-5" />
                              )}
                            </Button>
                          }
                        />
                      )}
                      <ZapButton
                        event={track.event}
                        size="icon"
                        className="h-12 w-12 rounded-full bg-white/[0.06] hover:bg-white/[0.12]"
                      />
                    </div>
                  )}
                </div>

                {/* Desktop Play Button, Edit Button, Like Button, Share Button, Add to Playlist, and Zap Stats */}
                <div className="hidden md:flex items-center gap-4">
                  {/* Play/Pause Button */}
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => playTrack(track)}
                    className="h-14 w-14 rounded-full border-0 bg-white text-black hover:bg-white/90 [&_svg]:!text-black"
                  >
                    {isCurrentlyPlaying ? (
                      <Pause className="h-6 w-6" />
                    ) : (
                      <Play className="h-6 w-6 ml-0.5" />
                    )}
                  </Button>

                  {/* Edit Button - Only show if user is the creator */}
                  {isCreator && (
                    <UploadTrackDialog editTrack={track}>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-14 w-14 rounded-full bg-white/[0.07] hover:bg-white/[0.12]"
                      >
                        <Edit className="h-5 w-5" />
                      </Button>
                    </UploadTrackDialog>
                  )}

                  {/* Like Button */}
                  <LikeButton
                    event={track.event}
                    size="icon"
                    className="h-14 w-14 bg-white/[0.07] hover:bg-white/[0.12]"
                  />

                  {/* Share Button */}
                  <ShareButton
                    track={track}
                    size="icon"
                    variant="ghost"
                    className="h-14 w-14 rounded-full bg-white/[0.07] hover:bg-white/[0.12]"
                  />

                  {/* Add to Playlist Button - Only show if user is logged in */}
                  {user && (
                    <AddToPlaylistDialog
                      track={track}
                      trigger={
                        <Button
                          size="icon"
                          variant="ghost"
                          className={`h-14 w-14 rounded-full ${
                            isInPlaylist
                              ? 'bg-amber-500/20 hover:bg-amber-500/30 text-amber-500'
                              : 'bg-white/[0.07] hover:bg-white/[0.12]'
                          }`}
                        >
                          {isInPlaylist ? (
                            <ListCheck className="h-5 w-5" />
                          ) : (
                            <ListPlus className="h-5 w-5" />
                          )}
                        </Button>
                      }
                    />
                  )}

                  {/* Zap Stats */}
                  <div className="flex items-center gap-3">
                    {/* Zap Button */}
                    <ZapButton
                      event={track.event}
                      size="icon"
                      className="h-14 w-14 rounded-full bg-white/[0.07] hover:bg-white/[0.12]"
                    />

                    <div>
                      <p className="text-2xl font-bold">
                        {formatSats(totalZaps)} sats
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {zapCount} {zapCount === 1 ? 'zap' : 'zaps'}
                      </p>
                    </div>
                  </div>
                </div>


              </div>

              {/* Debug Area - Only visible for track creators */}
              {isCreator && (
                <Collapsible
                  open={isDebugOpen}
                  onOpenChange={setIsDebugOpen}
                  className="w-full"
                >
                  <CollapsibleTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 opacity-20 hover:opacity-40 transition-opacity"
                    >
                      {isDebugOpen ? (
                        <ChevronUp className="h-3 w-3" />
                      ) : (
                        <Bug className="h-3 w-3" />
                      )}
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-4">
                    <div className="space-y-4">
                      {/* Event ID */}
                      <div className="space-y-1">
                        <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                          Event ID
                        </div>
                        <div className="text-[10px] font-mono bg-white/[0.02] p-2 rounded break-all">
                          {track.event.id}
                        </div>
                      </div>

                      {/* Kind */}
                      <div className="space-y-1">
                        <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                          Kind
                        </div>
                        <div className="text-[10px] font-mono bg-white/[0.02] p-2 rounded">
                          {track.event.kind}
                        </div>
                      </div>

                      {/* Pubkey */}
                      <div className="space-y-1">
                        <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                          Pubkey
                        </div>
                        <div className="text-[10px] font-mono bg-white/[0.02] p-2 rounded break-all">
                          {track.event.pubkey}
                        </div>
                      </div>

                      {/* Created At */}
                      <div className="space-y-1">
                        <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                          Created At
                        </div>
                        <div className="text-[10px] font-mono bg-white/[0.02] p-2 rounded">
                          {track.event.created_at} ({new Date(track.event.created_at * 1000).toLocaleString()})
                        </div>
                      </div>

                      {/* Content */}
                      <div className="space-y-1">
                        <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                          Content
                        </div>
                        <div className="text-[10px] font-mono bg-white/[0.02] p-2 rounded whitespace-pre-wrap break-words max-h-32 overflow-y-auto">
                          {track.event.content || '(empty)'}
                        </div>
                      </div>

                      {/* Tags */}
                      <div className="space-y-1">
                        <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                          Tags ({track.event.tags.length})
                        </div>
                        <div className="text-[10px] font-mono bg-white/[0.02] p-2 rounded max-h-64 overflow-y-auto">
                          <pre className="whitespace-pre-wrap break-all">
                            {JSON.stringify(track.event.tags, null, 2)}
                          </pre>
                        </div>
                      </div>

                      {/* Signature */}
                      <div className="space-y-1">
                        <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                          Signature
                        </div>
                        <div className="text-[10px] font-mono bg-white/[0.02] p-2 rounded break-all">
                          {track.event.sig}
                        </div>
                      </div>

                      {/* Full JSON */}
                      <div className="space-y-1">
                        <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                          Full Event JSON
                        </div>
                        <div className="text-[10px] font-mono bg-white/[0.02] p-2 rounded max-h-64 overflow-y-auto">
                          <pre className="whitespace-pre-wrap break-all">
                            {JSON.stringify(track.event, null, 2)}
                          </pre>
                        </div>
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              )}

              {/* Tabs Section */}
              <Tabs defaultValue="zappers" className="w-full">
                <TabsList className="h-auto glass border-0 p-1 rounded-full mb-6">
                  <TabsTrigger
                    value="zappers"
                    className="text-xs px-3 py-1.5 h-7 rounded-full data-[state=active]:glass-strong"
                  >
                    Top Zappers
                  </TabsTrigger>
                  <TabsTrigger
                    value="comments"
                    className="text-xs px-3 py-1.5 h-7 rounded-full data-[state=active]:glass-strong"
                  >
                    Comments
                    <Badge variant="secondary" className="ml-2 h-4 px-1.5 text-[10px] leading-none bg-white/10 hover:bg-white/10">
                      {commentCount}
                    </Badge>
                  </TabsTrigger>
                  <TabsTrigger
                    value="reactions"
                    className="text-xs px-3 py-1.5 h-7 rounded-full data-[state=active]:glass-strong"
                  >
                    Reactions
                    <Badge variant="secondary" className="ml-2 h-4 px-1.5 text-[10px] leading-none bg-white/10 hover:bg-white/10">
                      {likeCount}
                    </Badge>
                  </TabsTrigger>
                  {hasLyrics && (
                    <TabsTrigger
                      value="lyrics"
                      className="text-xs px-3 py-1.5 h-7 rounded-full data-[state=active]:glass-strong"
                    >
                      Lyrics
                    </TabsTrigger>
                  )}
                </TabsList>

                <TabsContent value="zappers" className="mt-0">
                  {topZappers.length === 0 ? (
                    <Card className="glass border-0">
                      <CardContent className="p-6">
                        <p className="text-sm text-muted-foreground text-center py-8">
                          No zaps yet. Be the first!
                        </p>
                      </CardContent>
                    </Card>
                  ) : (
                    <Card className="glass border-0">
                      <CardContent className="p-4 space-y-1">
                        {topZappers.map((zap, i) => (
                          <ZapperCard
                            key={i}
                            pubkey={zap.zapper}
                            amount={zap.amount}
                            comment={zap.comment}
                            rank={i + 1}
                          />
                        ))}
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                <TabsContent value="comments" className="mt-0">
                  <CommentsSection root={track.event} title="" />
                </TabsContent>

                <TabsContent value="reactions" className="mt-0">
                  {likeCount === 0 ? (
                    <Card className="glass border-0">
                      <CardContent className="p-6">
                        <p className="text-sm text-muted-foreground text-center py-8">
                          No reactions yet. Be the first!
                        </p>
                      </CardContent>
                    </Card>
                  ) : (
                    <Card className="glass border-0">
                      <CardContent className="p-4 space-y-1">
                        {likes?.map((like) => (
                          <ReactionCard
                            key={like.id}
                            pubkey={like.pubkey}
                          />
                        ))}
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                {hasLyrics && (
                  <TabsContent value="lyrics" className="mt-0">
                    <Card className="glass border-0">
                      <CardContent className="p-6">
                        <div className="whitespace-pre-wrap break-words text-sm leading-relaxed">
                          {lyricsTag}
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>
                )}
              </Tabs>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Player Bar */}
      <PlayerBar />
    </div>
  );
}

function ZapperCard({
  pubkey,
  amount,
  comment,
  rank,
}: {
  pubkey: string;
  amount: number;
  comment?: string;
  rank: number;
}) {
  const author = useAuthor(pubkey);
  const metadata = author.data?.metadata;
  const displayName = metadata?.name || genUserName(pubkey);
  const avatarUrl = metadata?.picture;

  return (
    <div className="flex items-start gap-3 p-2 rounded-md hover:bg-accent/50 transition-colors cursor-pointer">
      <div className="flex items-center gap-3 flex-shrink-0">
        <div className="flex items-center justify-center h-6 w-6 rounded-full bg-primary/20 text-xs font-bold text-primary">
          {rank}
        </div>
        <Link to={`/${nip19.npubEncode(pubkey)}`}>
          <Avatar className="h-8 w-8 hover:opacity-80 transition-opacity">
            <AvatarImage src={avatarUrl} alt={displayName} />
            <AvatarFallback>{displayName.slice(0, 2).toUpperCase()}</AvatarFallback>
          </Avatar>
        </Link>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-1">
          <Link
            to={`/${nip19.npubEncode(pubkey)}`}
            className="text-sm font-medium truncate hover:underline"
          >
            {displayName}
          </Link>
          <Badge variant="secondary" className="gap-1 flex-shrink-0 bg-primary/10">
            <Zap className="h-3 w-3 fill-current" />
            {formatSats(amount)}
          </Badge>
        </div>
        {comment && (
          <p className="text-xs text-muted-foreground whitespace-pre-wrap break-words">
            {comment}
          </p>
        )}
      </div>
    </div>
  );
}

function ReactionCard({ pubkey }: { pubkey: string }) {
  const author = useAuthor(pubkey);
  const metadata = author.data?.metadata;
  const displayName = metadata?.name || genUserName(pubkey);
  const avatarUrl = metadata?.picture;

  return (
    <div className="flex items-center gap-3 p-2 rounded-md hover:bg-accent/50 transition-colors cursor-pointer">
      <Heart className="h-5 w-5 fill-rose-500 text-rose-500 flex-shrink-0" />
      <Link to={`/${nip19.npubEncode(pubkey)}`}>
        <Avatar className="h-8 w-8 hover:opacity-80 transition-opacity">
          <AvatarImage src={avatarUrl} alt={displayName} />
          <AvatarFallback>{displayName.slice(0, 2).toUpperCase()}</AvatarFallback>
        </Avatar>
      </Link>
      <div className="flex-1 min-w-0">
        <Link
          to={`/${nip19.npubEncode(pubkey)}`}
          className="text-sm font-medium truncate hover:underline"
        >
          {displayName}
        </Link>
      </div>
    </div>
  );
}

function TrackPageSkeleton() {
  return (
    <div className="min-h-screen bg-background md:pb-24">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-white/[0.11] backdrop-blur-md">
        <div className="w-full px-4 py-2 md:px-6 md:py-2">
          <div className="flex items-center justify-between">
            <Skeleton className="h-8 w-24" />
            <div className="flex items-center gap-3">
              <Skeleton className="h-9 w-32" />
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="relative">
        <div className="relative mx-auto px-4 py-8 max-w-4xl 2xl:max-w-5xl">
          <div className="grid md:grid-cols-[300px,1fr] gap-8 max-w-lg md:max-w-none mx-auto">
            <Skeleton className="w-full aspect-square rounded-lg" />
            <div className="space-y-8">
              {/* Track Info */}
              <div className="space-y-6">
                <div>
                  <Skeleton className="h-10 w-3/4 mb-2" />
                  <div className="hidden md:flex items-center gap-2">
                    <Skeleton className="h-8 w-8 rounded-full" />
                    <Skeleton className="h-6 w-32" />
                  </div>
                  <Skeleton className="h-5 w-24 md:hidden" />
                </div>
                <div className="md:hidden space-y-4">
                  <div className="flex items-center justify-center gap-3">
                    <Skeleton className="h-12 w-12 rounded-full" />
                    <Skeleton className="h-16 w-16 rounded-full" />
                    <Skeleton className="h-12 w-12 rounded-full" />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Skeleton className="h-6 w-20 rounded-full" />
                  <Skeleton className="h-6 w-20 rounded-full" />
                  <Skeleton className="h-6 w-24 rounded-full" />
                </div>
                <div className="hidden md:flex items-center gap-4">
                  <Skeleton className="h-14 w-14 rounded-full" />
                  <Skeleton className="h-14 w-14 rounded-full" />
                  <div className="space-y-1">
                    <Skeleton className="h-8 w-32" />
                    <Skeleton className="h-4 w-20" />
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <div className="space-y-4">
                <Skeleton className="h-8 w-64 rounded-full" />
                <div className="space-y-3">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 rounded-lg">
                      <Skeleton className="h-8 w-8 rounded-full flex-shrink-0" />
                      <Skeleton className="h-10 w-10 rounded-full flex-shrink-0" />
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center justify-between">
                          <Skeleton className="h-4 w-32" />
                          <Skeleton className="h-5 w-16 rounded-full" />
                        </div>
                        <Skeleton className="h-4 w-full" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
